import type {ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {CalendarDays, Clock, KeyRound, MapPin, Users, Wifi} from 'lucide-react';
import {AssistantPanel} from '@chekinapp/ui';
import {useReservation} from 'context/reservation';
import {useFetchHousingLocation, useGuestGroup} from 'hooks';
import {formatDisplayDate} from 'utils/dates';
import {useVelaChatDraftStore} from '../hooks/useVelaChatDraftStore';

const COMMON_QUESTIONS = [
  {id: 'wifi', icon: <Wifi />, key: 'ask.wifi'},
  {id: 'entry', icon: <KeyRound />, key: 'ask.entry'},
  {id: 'early_check_in', icon: <Clock />, key: 'ask.early_check_in'},
];

function VelaChatContext() {
  const {t, i18n} = useTranslation('vela');
  const {data: reservation, housing} = useReservation();
  const {data: housingLocation} = useFetchHousingLocation({
    housingLocation: housing?.location,
  });
  const setDraft = useVelaChatDraftStore(state => state.setDraft);
  const {guestGroup} = useGuestGroup();

  const checkIn = formatDisplayDate(reservation?.check_in_date, i18n.language);
  const checkOut = formatDisplayDate(reservation?.check_out_date, i18n.language);
  const dates = checkIn && checkOut ? `${checkIn} – ${checkOut}` : checkIn || checkOut;
  const guestCount = guestGroup?.known_number_of_guests;
  const property = housingLocation?.city;

  const rows = [
    dates && {
      id: 'dates',
      icon: <CalendarDays />,
      label: t('booking.dates'),
      value: dates,
    },
    typeof guestCount === 'number' && {
      id: 'guests',
      icon: <Users />,
      label: t('booking.guests'),
      value: String(guestCount),
    },
    property && {
      id: 'property',
      icon: <MapPin />,
      label: t('booking.property'),
      value: property,
    },
  ].filter(Boolean) as {id: string; icon: ReactNode; label: string; value: string}[];

  const handleAsk = (question: string) => {
    setDraft(question);
  };

  return (
    <>
      {rows.length > 0 && (
        <AssistantPanel.Section
          label={t('section.your_booking')}
          className="hidden lg:flex"
        >
          <div className="flex flex-col gap-[11px]">
            {rows.map(row => (
              <div key={row.id} className="flex items-center gap-[11px] text-[12.5px]">
                <span className="grid size-[30px] flex-none place-items-center rounded-[9px] bg-[var(--assistant-tip-icon-bg)] text-[var(--assistant-tip-icon)] [&_svg]:size-[15px]">
                  {row.icon}
                </span>
                <span className="flex-none font-medium text-[var(--assistant-tip-description)]">
                  {row.label}
                </span>
                <span className="ml-auto pl-2 text-right font-semibold leading-snug text-[var(--assistant-tip-title)]">
                  {row.value}
                </span>
              </div>
            ))}
          </div>
        </AssistantPanel.Section>
      )}

      <AssistantPanel.Section label={t('section.common_questions')}>
        <AssistantPanel.Actions>
          {COMMON_QUESTIONS.map(question => {
            const label = t(question.key);

            return (
              <AssistantPanel.Action
                key={question.id}
                icon={question.icon}
                title={label}
                onClick={() => handleAsk(label)}
              />
            );
          })}
        </AssistantPanel.Actions>
      </AssistantPanel.Section>
    </>
  );
}

export {VelaChatContext};
